import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useWorkflows } from "../hooks/useWorkflows";

const TRIGGER_LABELS = {
    on_submit: "On Submit",
    on_high_score: "High Lead Score",
    on_keyword: "Keyword Match",
    on_custom_condition: "Custom Condition"
};

function TriggerBadge({ trigger }) {
    const colors = {
        on_submit: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
        on_high_score: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
        on_keyword: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
        on_custom_condition: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
    };
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[trigger] || "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"}`}>
            {TRIGGER_LABELS[trigger] || trigger}
        </span>
    );
}

function StepCountBadge({ count }) {
    return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
            </svg>
            {count} step{count !== 1 ? "s" : ""}
        </span>
    );
}

function EnabledToggle({ enabled, onChange, disabled }) {
    return (
        <button
            onClick={onChange}
            disabled={disabled}
            className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${enabled ? "bg-indigo-600" : "bg-gray-200 dark:bg-gray-600"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
        >
            <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${enabled ? "translate-x-4" : "translate-x-0"}`} />
        </button>
    );
}

function WorkflowCard({ workflow, onToggle, onDelete, toggling }) {
    const navigate = useNavigate();
    const { formId } = useParams();
    const [confirmDelete, setConfirmDelete] = useState(false);

    return (
        <div className="bg-[#ffffff] dark:bg-gray-800 rounded-xl border-[1px] border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3
                            className="text-base font-semibold text-gray-900 dark:text-white truncate cursor-pointer hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                            onClick={() => navigate(`/form-builder/${formId}/workflows/${workflow._id}`)}
                        >
                            {workflow.name}
                        </h3>
                        <TriggerBadge trigger={workflow.trigger} />
                    </div>
                    <div className="flex items-center gap-3 mt-1.5">
                        <StepCountBadge count={workflow.steps?.length || 0} />
                        <span className="text-sm text-gray-600 dark:text-gray-400 dark:text-gray-500">v{workflow.version || 1}</span>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <EnabledToggle
                        enabled={workflow.enabled}
                        onChange={() => onToggle(workflow._id, !workflow.enabled)}
                        disabled={toggling === workflow._id}
                    />
                    <button
                        onClick={() => navigate(`/form-builder/${formId}/workflows/${workflow._id}`)}
                        className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded transition-colors"
                        title="Edit workflow"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    <button
                        onClick={() => navigate(`/form-builder/${formId}/workflows/${workflow._id}/runs`)}
                        className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                        title="View run history"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                    </button>
                    {confirmDelete ? (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => { onDelete(workflow._id); setConfirmDelete(false); }}
                                className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                            >
                                Confirm
                            </button>
                            <button
                                onClick={() => setConfirmDelete(false)}
                                className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => setConfirmDelete(true)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            title="Delete workflow"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function CostSummaryCard({ formId }) {
    const { costSummary, loading } = useWorkflows();
    const [data, setData] = useState(null);

    useEffect(() => {
        costSummary(formId).then(d => { if (d) setData(d); });
    }, [formId]); // eslint-disable-line

    if (loading || !data) return null;

    return (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border-[1px] border-indigo-100 dark:border-indigo-800 p-4">
            <p className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wide mb-3">This Month</p>
            <div className="grid grid-cols-3 gap-4">
                <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{data.totalRuns || 0}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Total runs</p>
                </div>
                <div>
                    <p className="text-xl font-bold text-green-600 dark:text-green-400">{data.succeeded || 0}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Succeeded</p>
                </div>
                <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                        ${((data.totalCostCents || 0) / 100).toFixed(4)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">AI cost</p>
                </div>
            </div>
            {data.failed > 0 && (
                <p className="text-xs text-red-500 dark:text-red-400 mt-2">{data.failed} failed run{data.failed !== 1 ? "s" : ""} this month</p>
            )}
        </div>
    );
}

export default function WorkflowList() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { listWorkflows, updateWorkflow, deleteWorkflow, loading, error } = useWorkflows();
    const [workflows, setWorkflows] = useState([]);
    const [toggling, setToggling] = useState(null);

    const load = useCallback(async () => {
        const data = await listWorkflows(formId);
        if (data) setWorkflows(data);
    }, [formId]); // eslint-disable-line

    useEffect(() => { load(); }, [load]);

    const handleToggle = async (id, enabled) => {
        setToggling(id);
        await updateWorkflow(id, { enabled });
        await load();
        setToggling(null);
    };

    const handleDelete = async (id) => {
        await deleteWorkflow(id);
        setWorkflows(prev => prev.filter(w => w._id !== id));
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#181d33] font-Rajdhani dark:text-white">
            <div className="max-w-3xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <Link
                            to={`/form-builder/:formId/responses`.replace(":formId", formId)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </Link>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Agent Workflows</h1>
                            <p className="text-base text-gray-500 dark:text-gray-400 mt-0.5">Automate actions after every submission</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => navigate(`/form-builder/${formId}/recipes`)}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            Recipes
                        </button>
                        <button
                            onClick={() => navigate(`/form-builder/${formId}/workflows/new`)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors shadow-sm"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            New Workflow
                        </button>
                    </div>
                </div>

                {/* Cost summary */}
                <div className="mb-6">
                    <CostSummaryCard formId={formId} />
                </div>

                {/* Content */}
                {loading && workflows.length === 0 ? (
                    <div className="text-center py-16 text-gray-400 dark:text-gray-500">
                        <div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto mb-3" />
                        Loading workflows…
                    </div>
                ) : error ? (
                    <div className="bg-red-50 dark:bg-red-900/20 border-[1px] border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 rounded-xl px-4 py-3 text-sm">
                        {error}
                    </div>
                ) : workflows.length === 0 ? (
                    <div className="text-center py-16">
                        <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">No workflows yet</h3>
                        <p className="text-base text-gray-500 dark:text-gray-400 mb-5 max-w-xs mx-auto">
                            Create your first workflow to automate actions when submissions arrive.
                        </p>
                        <button
                            onClick={() => navigate(`/form-builder/${formId}/workflows/new`)}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            Create Workflow
                        </button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {workflows.map(w => (
                            <WorkflowCard
                                key={w._id}
                                workflow={w}
                                onToggle={handleToggle}
                                onDelete={handleDelete}
                                toggling={toggling}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
